import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { DateTime } from 'luxon';
import { debounceTime, Observable, of, Subscription, take } from 'rxjs';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { InputTrimDirective } from '../../common/directives/input-trim.directive';
import { TrackByPropertyPipe } from '../../common/pipes/track-by-property/track-by-property.pipe';
import {
    getDateValidators, getEmailValidators, dateFormat, minBirthDate, parseDateOfBirthValue, togglePasswordVisibility, isFieldRequired,
    updateFieldValidators, revalidateEmailComplianceIfUnedited, isPasswordAllowed, buildAttributesAsDtos, getExistingAttributeValue,
    revalidateRestoredControls
} from './universal-id-details.utils';
import { matchPassword } from '../../common/validators/match-password/match-password.validator';
import { passwordRegexValidator } from '../../common/validators/password-regex/password-regex.validator';
import { UserSearchService } from '../../common/services/user-search.service';
import { remoteValueExistsValidator } from '../../common/validators/remote-value-exists/remote-value-exists.validator';
import { emailDomainValidator } from '../../common/validators/email-domain/email-domain.validator';
import { sequentialAsyncValidators } from '../../common/validators/sequential-async/sequential-async.validator';
import { SsoUserCreationRequest } from '../../common/interfaces/sso-user-creation-request';
import { dateArrayToDashSeparatedString } from '../../common/utils/date-util';
import { MmDatepickerComponent } from '../mm-datepicker/mm-datepicker.component';
import { PasswordPolicyService } from '../../common/services/password-policy.service';
import { EmailDomainPolicyService } from '../../common/services/email-domain-policy.service';
import { PasswordPolicyDto } from '../../common/interfaces/password-policy-dto';
import { EmailDomainPolicyDto } from '../../common/interfaces/email-domain-policy-dto';
import { OrganizationUserDto } from '../../common/interfaces/organization-user-dto';
import { UniversalIdFields } from '../../common/enums/universal-id-fields.enum';
import { SsoUserDetailsDto } from '../../common/interfaces/sso-user-details-dto';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { UserRoleEnum } from '../../common/enums/user-role.enum';
import { EnterpriseLabels } from '../../common/interfaces/enterprise-labels';
import { UniversalIdFormStateService } from '../../common/services/universal-id-form-state.service';
import { UniversalIdStatusesComponent } from '../universal-id-statuses/universal-id-statuses.component';
import { SsoUserAttributeTypeDto } from '../../common/interfaces/sso-user-attribute-type-dto';
import { IdpUnlinkService } from '../../common/services/idp-unlink/idp-unlink.service';
import { ToastrService } from 'ngx-toastr';
import { IdentityBrokeringPolicyDto } from '../../common/interfaces/identity-brokering-policy-dto';

const namePattern = '^[-A-Za-zÀ-ÖØ-öø-ÿ0-9_@. \'"]+$';
const phonePattern = '([\\(](\\d{3})[\\)]|(\\d{3}))?[-. ]?(\\d{3})[-. ]?(\\d{4})([-. ]?[X|x]?(\\d{1,5}))?';

@Component({
    selector: 'lib-universal-id-details',
    standalone: true,
    imports: [
        NgIf, NgFor, ReactiveFormsModule, NgClass, MmDatepickerComponent,
        TrackByPropertyPipe, FaIconComponent, NgbTooltip,
        InputTrimDirective, UniversalIdStatusesComponent
    ],
    templateUrl: './universal-id-details.component.html',
    styleUrl: './universal-id-details.component.scss'
})
export class UniversalIdDetailsComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    private formBuilder = inject(FormBuilder);
    private passwordPolicyService = inject(PasswordPolicyService);
    private userSearchService = inject(UserSearchService);
    private emailDomainsService = inject(EmailDomainPolicyService);
    private datePipe = inject(DatePipe);
    private formStateService = inject(UniversalIdFormStateService);
    private idpUnlinkService = inject(IdpUnlinkService);
    private toastrService = inject(ToastrService);
    private formValueChangesSubscription = new Subscription();
    static readonly USERNAME_MAX_LENGTH = 75;
    private static readonly ENTERPRISE_PRIMARY_ADMIN_ROLE = 'EnterprisePrimaryAdmin';
    protected readonly faInfoCircle = faInfoCircle;
    @Input() ssoUserDetails?: SsoUserDetailsDto | null;
    @Input() currentEnterpriseId: number | undefined;
    @Input() enterpriseLabels!: EnterpriseLabels;
    @Input() isCreateMode = true;
    @Input() isCreateModeUniversalIdFlow = false;
    @Input() triggerAutopopulate = false;
    @Input() selectedAccounts: OrganizationUserDto[] = [];
    @Input() idpAttributes: SsoUserAttributeTypeDto[] = [];
    @Input() identityBrokeringPolicy: IdentityBrokeringPolicyDto | null = null;

    @Output() universalIdDetailsFormUpdated = new EventEmitter<{
        value: Partial<SsoUserCreationRequest>,
        valid: boolean,
        dirty: boolean
    }>();

    dateOfBirthMin = DateTime.fromFormat(minBirthDate, dateFormat).toJSDate();
    dateOfBirthMax = DateTime.now().toJSDate();
    pwdPattern: PasswordPolicyDto | undefined;
    emailPattern: EmailDomainPolicyDto | undefined;
    passwordError: string[] = [];
    emailError = '';
    isLoadingPasswordValidators = false;
    isLoadingEmailDomainValidator = false;
    isValidatingUsername = false;
    isValidatingEmail = false;
    passwordTooltipText = 'Leave blank to remain unchanged';
    isReadOnly = false;
    isLocked = false;
    isEnterpriseAdminHidden = false;
    passwordAllowed = true;

    get dobRequired(): boolean {
        return isFieldRequired(this.identityBrokeringPolicy, 'dobRequired');
    }

    get emailRequired(): boolean {
        return isFieldRequired(this.identityBrokeringPolicy, 'emailRequired');
    }

    form: FormGroup = new FormGroup({
        firstName: new FormControl(''),
        lastName: new FormControl(''),
        dateOfBirth: new FormControl(false),
        username: new FormControl(''),
        email: new FormControl(''),
        phone: new FormControl(''),
        unlock: new FormControl(false),
        enterpriseAdmin: new FormControl(false),
        password: new FormControl(''),
        confirmPassword: new FormControl('')
    });

    universalIdFieldFormMap = new Map<string, string>([
        ['firstName', UniversalIdFields.FIRST_NAME],
        ['lastName', UniversalIdFields.LAST_NAME],
        ['dateOfBirth', UniversalIdFields.DOB],
        ['email', UniversalIdFields.EMAIL],
        ['phone', UniversalIdFields.PHONE],
        ['username', UniversalIdFields.USERNAME]
    ]);

    ngOnInit() {
        this.isReadOnly = !this.isCreateMode && !this.ssoUserDetails?.enabled;
        this.isLocked = !this.isCreateMode && !!this.ssoUserDetails?.locked;
        this.isEnterpriseAdminHidden = !this.isCreateMode && !!this.ssoUserDetails?.roleList?.includes(UniversalIdDetailsComponent.ENTERPRISE_PRIMARY_ADMIN_ROLE);
        this.form = this.formBuilder.group({
            firstName: ['', [Validators.required, Validators.maxLength(100), Validators.pattern(namePattern)]],
            lastName: ['', [Validators.required, Validators.maxLength(100), Validators.pattern(namePattern)]],
            dateOfBirth: [{ value: '', disabled: this.isReadOnly }, getDateValidators(this.dobRequired)],
            username: ['', {
                validators: [Validators.required, Validators.maxLength(UniversalIdDetailsComponent.USERNAME_MAX_LENGTH)],
                asyncValidators: [
                    remoteValueExistsValidator(
                        this.isUsernameUnique.bind(this),
                        false,
                        () => this.isValidatingUsername = true, () => this.isValidatingUsername = false
                    )
                ]
            }],
            email: ['', {
                validators: getEmailValidators(this.emailRequired),
                asyncValidators: [
                    sequentialAsyncValidators([
                        emailDomainValidator(() => this.getEmailDomainPolicy(this.currentEnterpriseId!), (valError: string) =>
                            this.emailError = valError, () =>
                            this.isLoadingEmailDomainValidator = true, () => this.isLoadingEmailDomainValidator = false),
                        remoteValueExistsValidator(this.isEmailUnique.bind(this), false, () =>
                            this.isValidatingEmail = true, () => this.isValidatingEmail = false)
                    ])
                ]
            }],
            phone: ['', [Validators.required, Validators.minLength(10), Validators.pattern(phonePattern)]],
            unlock: [{ value: false, disabled: this.isReadOnly }],
            enterpriseAdmin: [{ value: false, disabled: this.isReadOnly || this.isEnterpriseAdminHidden }],
            password: ['', {
                validators: [(this.isCreateMode && this.passwordAllowed) ? Validators.required : Validators.nullValidator],
                asyncValidators: [
                    passwordRegexValidator(() => this.getPasswordConfiguration(this.currentEnterpriseId!), (totalErrors: string[]) =>
                        this.passwordError = totalErrors, () =>
                        this.isLoadingPasswordValidators = true, () => this.isLoadingPasswordValidators = false)
                ]
            }],
            confirmPassword: ['', [(this.isCreateMode && this.passwordAllowed) ? Validators.required : Validators.nullValidator]]
        }, {
            validators: [matchPassword('password', 'confirmPassword')]
        });

        this.loadUniversalIdData();

        this.formValueChangesSubscription = this.form.valueChanges.pipe(debounceTime(300)).subscribe(value => {
            if(this.isCreateModeUniversalIdFlow){
                this.formStateService.setFormState(value);
            }
            this.emitFormUpdate();
        });

        this.formValueChangesSubscription.add(
            this.form.statusChanges.pipe(
                distinctUntilChanged(),
                filter(status => status !== 'PENDING')
            ).subscribe(() => this.emitFormUpdate())
        );

        this.loadSavedFormData();
    }

    private emitFormUpdate(): void {
        const formValue = { ...this.form.value, ssoUserAttributes: buildAttributesAsDtos(this.idpAttributes, this.form, this.ssoUserDetails) };
        this.universalIdDetailsFormUpdated.emit({ value: formValue, valid: this.form.valid, dirty: this.form.dirty });
    }

    ngOnDestroy(): void {
        this.formValueChangesSubscription.unsubscribe();
    }


    ngAfterViewInit(): void {
        if (this.isCreateMode && this.selectedAccounts.length > 0) {
            this.autoPopulateFieldsIfEligible();
        } else if (!this.isCreateMode) {
            this.form.markAllAsTouched();
            this.form.updateValueAndValidity();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        const triggerChange = changes[ 'triggerAutopopulate' ];
        if (this.isCreateModeUniversalIdFlow && triggerChange && triggerChange.previousValue === false && triggerChange.currentValue === true) {
            this.autoPopulateWithFirstSelectedAccount();
        }

        const idpAttributesChange = changes['idpAttributes'];
        if (idpAttributesChange && idpAttributesChange.currentValue) {
            this.addIdpAttributeControls();
        }

        const policyChange = changes['identityBrokeringPolicy'];
        if (policyChange && this.form) {
            this.passwordAllowed = isPasswordAllowed(this.identityBrokeringPolicy);
            updateFieldValidators(this.form, this.dobRequired, this.emailRequired, this.isCreateMode, this.passwordAllowed);
        }
    }

    private addIdpAttributeControls(): void {
        for (const attr of this.idpAttributes) {
            const controlName = `idp_${attr.name}`;
            if (!this.form.contains(controlName)) {
                const existingValue = getExistingAttributeValue(this.ssoUserDetails, attr.name);
                this.form.addControl(controlName, new FormControl(existingValue));
            }
        }
    }


    autoPopulateWithFirstSelectedAccount(): void {
        const firstSelectedAccount = this.selectedAccounts[0];
        for (const [field, property] of this.universalIdFieldFormMap) {
            const control = this.form.get(field);

            if (!control) continue;

            const currentValue = control.value;
            const isEmpty = currentValue === null || currentValue === undefined || currentValue === '';

            if (isEmpty) {
                const newValue = firstSelectedAccount[property as keyof OrganizationUserDto] ?? null;
                if (newValue !== null && newValue !== undefined) {
                    this.updateFieldValue(field, newValue);
                }
            }
        }
    }

    autoPopulateFieldsIfEligible(): void {
        const referenceUser = this.selectedAccounts[0];
        for (const [field, property] of this.universalIdFieldFormMap) {
            const referenceValueOriginal = referenceUser[property as keyof OrganizationUserDto]?.toString() ?? '';
            const referenceValueLowercase = referenceValueOriginal.toLowerCase();

            let shouldUpdate = referenceValueOriginal !== null;

            if (this.selectedAccounts.length > 1) {
                shouldUpdate = this.selectedAccounts.slice(1).every(user => {
                    const currentValue = user[property as keyof OrganizationUserDto]?.toString() ?? '';
                    return currentValue.toLowerCase() === referenceValueLowercase;
                });
            }

            if (shouldUpdate) {
                this.updateFieldValue(field, referenceValueOriginal);
            }
        }
    }

    private updateFieldValue(field: string, value: string | number | Date | number[]) {
        const control = this.form.get(field);
        if (value && control) {
            control.patchValue(field === 'dateOfBirth' ? parseDateOfBirthValue(value) : value);
            control.markAsDirty();
            control.updateValueAndValidity();
        }
    }

    unlinkExternalIdp() {
        if (!this.ssoUserDetails?.id) {
            return;
        }
        this.idpUnlinkService.unlink(this.ssoUserDetails).subscribe({
            next: () => {
                this.toastrService.success('3rd Party IDP Unlinked Successfully');
                if (this.ssoUserDetails) {
                    this.ssoUserDetails.externalIdpDto = undefined;
                }
            },
            error: () => {
                this.toastrService.error('Failed to Unlink 3rd Party IDP');
            }
        });
    }

    getPasswordConfiguration(enterpriseId: number): Observable<PasswordPolicyDto> {
        if (this.pwdPattern) {
            return of(this.pwdPattern);
        }
        return this.passwordPolicyService
            .getPasswordPolicy(enterpriseId)
            .pipe(tap(pwdPattern => this.pwdPattern = pwdPattern));
    }

    getEmailDomainPolicy(enterpriseId: number): Observable<EmailDomainPolicyDto> {
        if (this.emailPattern?.emailDomains.length) {
            return of(this.emailPattern);
        }
        return this.emailDomainsService
            .getEmailDomainPolicy(enterpriseId)
            .pipe(tap(emailPattern => this.emailPattern = emailPattern));
    }

    isUsernameUnique(username: string): Observable<boolean> {
        if(username === this.ssoUserDetails?.username) {
            return of(true);
        }
        return this.userSearchService.isUsernameUnique(this.currentEnterpriseId!, username);
    }

    isEmailUnique(email: string): Observable<boolean> {
        if(email === this.ssoUserDetails?.email) {
            return of(true);
        }
        return this.userSearchService.isEmailUnique(this.currentEnterpriseId!, email);
    }

    togglePassword(passwordInput: HTMLInputElement, passwordIcon: HTMLElement): void {
        togglePasswordVisibility(passwordInput, passwordIcon);
    }

    onKeydown() { /* NOOP */ }

    private loadUniversalIdData(): void {
        if (this.isCreateMode) {
            return;
        }

        const hasEnterpriseAdminRole = this.ssoUserDetails?.roleList?.includes(UserRoleEnum.ENTERPRISE_ADMIN) ?? false;
        const formattedDateOfBirth = this.datePipe.transform(dateArrayToDashSeparatedString(this.ssoUserDetails?.dateOfBirth), dateFormat);

        this.form.patchValue({
            firstName: this.ssoUserDetails?.firstName,
            lastName: this.ssoUserDetails?.lastName,
            dateOfBirth: formattedDateOfBirth ? new Date(formattedDateOfBirth) : '',
            username: this.ssoUserDetails?.username,
            email: this.ssoUserDetails?.email,
            phone: this.ssoUserDetails?.phone,
            enterpriseAdmin: hasEnterpriseAdminRole
        });

        revalidateEmailComplianceIfUnedited(this.form, this.isCreateMode);

        Promise.resolve().then(() => {
            this.universalIdDetailsFormUpdated.emit({
                value: this.form.value,
                valid: this.form.valid,
                dirty: this.form.dirty
            });
        });
    }

    private loadSavedFormData(): void {
        if (this.isCreateModeUniversalIdFlow) {
            this.formStateService.getFormData$().pipe(take(1)).subscribe(data => {
                if (data) {
                    this.form.patchValue(data);
                    revalidateRestoredControls(this.form, data as Record<string, unknown>);
                }
            });
        }
    }
}
